"use client";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Linkedin, Send } from "lucide-react";
import Header from "../../../Components/Header";
import Footer from "../../../Components/Footer";
import ContactForm from "../../../Components/ContactForm";

export default function ContactPage() {
  return (
    <div className="bg-gray-50 text-gray-900 min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-700 text-white py-28 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto px-6"
        >
          <h1 className="text-5xl font-extrabold mb-4">Contact Us</h1>
          <p className="text-lg text-indigo-100">
            We’d love to hear from you. Reach out for inquiries, partnerships,
            or a free profile review.
          </p>
        </motion.div>
      </section>

      {/* Intro + Quick CTA */}
      <section className="py-16 px-6 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-4">
              Let’s start a conversation
            </h2>
            <p className="text-gray-600 mb-6">
              Whether you’re ready for a full profile overhaul or just want a
              quick audit, our team is here to help you stand out to recruiters
              and hiring managers.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {/* Card: Email */}
              <div className="bg-white/80 backdrop-blur-lg border border-indigo-100 rounded-2xl p-5 shadow hover:shadow-md transition">
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="text-indigo-600" />
                  <h3 className="font-semibold">Email</h3>
                </div>
                <a
                  href="mailto:hello@profilewise.com"
                  className="text-indigo-600 hover:underline"
                >
                  hello@profilewise.com
                </a>
              </div>

              {/* Card: Phone */}
              <div className="bg-white/80 backdrop-blur-lg border border-indigo-100 rounded-2xl p-5 shadow hover:shadow-md transition">
                <div className="flex items-center gap-3 mb-2">
                  <Phone className="text-indigo-600" />
                  <h3 className="font-semibold">Phone / WhatsApp</h3>
                </div>
                <a
                  href="tel:+917057051648"
                  className="text-indigo-600 hover:underline"
                >
                  +91 7057051648
                </a>
              </div>

              {/* Card: Location */}
              <div className="bg-white/80 backdrop-blur-lg border border-indigo-100 rounded-2xl p-5 shadow hover:shadow-md transition">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="text-indigo-600" />
                  <h3 className="font-semibold">Location</h3>
                </div>
                <p className="text-gray-600">
                  Pune • Hinjawadi Pimpri-Chinchwad • Remote-first (India)
                </p>
              </div>

              {/* Card: Hours */}
              <div className="bg-white/80 backdrop-blur-lg border border-indigo-100 rounded-2xl p-5 shadow hover:shadow-md transition">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="text-indigo-600" />
                  <h3 className="font-semibold">Hours</h3>
                </div>
                <p className="text-gray-600">Mon–Sun, 24 Hours </p>
              </div>
            </div>

            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://www.linkedin.com/company/your-company"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-indigo-600 hover:underline"
              >
                <Linkedin size={18} /> Connect on LinkedIn
              </a>
              <a
                href="https://wa.me/917057051648?text=Hi%20I%20want%20to%20book%20a%20call"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-indigo-600 hover:underline"
              >
                <Send size={18} /> Book a Call
              </a>
            </div>
          </motion.div>

          {/* Pretty image / map placeholder */}
          <motion.img
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?q=80&w=1600&auto=format&fit=crop"
            alt="Get in touch"
            className="rounded-3xl shadow-2xl"
          />
        </div>
      </section>

      {/* Contact Form (reusing your component) */}
      <div className="max-w-6xl mx-auto px-6">
        <ContactForm />
      </div>

      {/* FAQ */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-bold mb-8 text-center"
        >
          Frequently Asked Questions
        </motion.h3>

        <div className="grid md:grid-cols-2 gap-6">
          {[
            {
              q: "How soon will I hear back after submitting the form?",
              a: "Within 24 hours on business days. We’ll review your links and send a short audit asap.",
            },
            {
              q: "Do you work with freshers as well as experienced candidates?",
              a: "Absolutely. We tailor the strategy to your stage—ATS-friendly for freshers and impact-driven for senior roles.",
            },
            {
              q: "What links should I share?",
              a: "LinkedIn and Naukri are perfect. If you have a portfolio or GitHub, include them too.",
            },
            {
              q: "Is the initial review really free?",
              a: "Yes—the quick audit is free. If you want a complete rewrite or advanced package, we’ll share options.",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl border border-indigo-100 p-6 shadow-sm"
            >
              <h4 className="font-semibold text-lg mb-2 text-indigo-700">
                {item.q}
              </h4>
              <p className="text-gray-600">{item.a}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
